import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, styled, Checkbox } from '@mui/material';
import { bakery2bTabState } from '../bakery2b.state.ts';
import { ControlButtons } from './controlButtons.component.tsx';
import { Table } from '../../../../../../shared/components/table/table.component.tsx';
import { storageHelper } from '../../../../../../shared/helpers/storage/index.ts';
import { ConnectBakery2bClientWithCustomer } from '../popups/connectBakery2bClientWithCustomer/connectBakery2bClientWithCustomer.popup.tsx';
import { Column } from '../../../../../../shared/components/table/components/common.components.tsx';
import { ITabState } from '../../../../states/tabState.model.ts';
import { SCellCheckboxContainer } from '../../conditions/components/sections/specialPrices/individualPrices/individualPrices.component.tsx';

export const Bakery2bTable: FC<IBakery2bTable> = ({
  bakery2bData,
  bakery2bAccountsOptions,
  customerId,
}) => {
  const { t } = useTranslation();
  const [tableData, setTableData] = useState(bakery2bData);
  const [accountsOptions, setAccountsOptions] = useState(
    (bakery2bAccountsOptions ?? []).filter(
      (item) =>
        !bakery2bData.find((acc: any) => {
          return acc.id == item.id;
        }),
    ),
  );
  const [selectedPos, setSelectedPos] = useState<ITabState['bakery2bAccounts'][number] | null>(
    null,
  );
  const connectBakery2bClientWithCustomerPopup = useMemo(
    () => new ConnectBakery2bClientWithCustomer(),
    [],
  );
  const [currentCustomerId, setCurrentCustomerId] = useState<string>(customerId);

  useEffect(() => {
    bakery2bTabState.pub.initBakery2bAccountsData(tableData, customerId);
    const bakery2bTabStateSub = bakery2bTabState.sub
      .state()
      .subscribe(({ bakery2bAccounts, customerId }) => {
        const filteredOptions = structuredClone(
          (bakery2bAccountsOptions ?? []).filter(
            (item) =>
              !bakery2bAccounts.find((acc: any) => {
                return acc.id == item.id;
              }),
          ),
        );
        setAccountsOptions(filteredOptions);
        setCurrentCustomerId(customerId);
        setTableData(bakery2bAccounts);
        setSelectedPos(null);
      });
    return () => {
      bakery2bTabStateSub.unsubscribe();
    };
  }, []);

  return (
    <>
      <Box>
        <ControlButtons
          {...{
            selectedPos,
            currentCustomerId,
            bakery2bData: tableData,
            connectBakery2bClientWithCustomerPopup,
            bakery2bAccountsOptions: accountsOptions,
          }}
        />
        <STable
          data={tableData}
          initialState={{
            columns: storageHelper.local.getItem('customer.bakery2bColumnModel'),
          }}
          onChangeColumnModel={(v) =>
            storageHelper.local.setItem('customer.bakery2bColumnModel', v)
          }
          onRowClick={(v) => {
            setSelectedPos(v.row);
          }}
          height={400}
        >
          <Column field='email' headerName={t('common.email')} sortable={false} width={380} />
          <Column field='name' headerName={t('common.name')} sortable={false} width={380} />
          <Column
            field='isDeactivated'
            headerName={t('customer.deactivated')}
            sortable={false}
            align='center'
            headerAlign='center'
            renderCell={({ value }) => (
              <SCellCheckboxContainer data-field='isDeactivated'>
                <Checkbox size='small' checked={value} />
              </SCellCheckboxContainer>
            )}
            width={110}
          />
        </STable>
      </Box>
      {<connectBakery2bClientWithCustomerPopup.Component />}
    </>
  );
};

const STable = styled(Table)({
  '& .MuiDataGrid-cell': {
    padding: 0,
  },
  '& .MuiDataGrid-cellContent': {
    height: '100%',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 10,
    paddingRight: 10,
    justifyContent: 'inherit',
  },
});

interface IBakery2bTable {
  bakery2bData: ITabState['bakery2bAccounts'];
  bakery2bAccountsOptions: ITabState['bakery2bTabOptions']['bakery2bAccounts'];
  customerId: string;
}
